<template>
  <div class="page-list-item" :class="{ 'is-selected': selected }" @click="handleClick">
    <div class="thumbnail-container" :style="thumbnailStyle">
      <canvas ref="thumbnailCanvas" class="thumbnail-canvas" :style="canvasFitStyle"></canvas>
      <div class="thumbnail-gradient"></div>
      <div class="page-badge" :class="{ 'is-cover': isCover || isBackCover }">
        <span class="badge-text">{{ label }}</span>
      </div>
    </div>
    <button v-if="deletable" class="delete-btn" title="删除" @click.stop="handleDelete">×</button>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import type { BookPageData } from '@/types';
  import { getScaleWH } from '@/utils/canvasUtil';

  interface Props {
    pageData: BookPageData;
    selected?: boolean;
    sourceCanvas?: HTMLCanvasElement | null;
    deletable?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    selected: false,
    sourceCanvas: null,
    deletable: true
  });

  const emit = defineEmits<{
    select: [];
    delete: [];
  }>();

  const isCover = computed(() => props.pageData.type === 'cover');
  const isBackCover = computed(() => props.pageData.type === 'backCover');

  const label = computed(() => {
    if (props.pageData.type === 'cover') return '封面';
    if (props.pageData.type === 'backCover') return '封底';
    if (props.pageData.index !== undefined) {
      return String(props.pageData.index).padStart(2, '0');
    }
    return '00';
  });

  /**
   * 容器宽高比：
   * - pageRatio >= 1（横版）：宽度 >= 高度，直接用页面自身比例
   * - pageRatio < 1（竖版）：高度 > 宽度，限制高度最大=宽度，容器为正方形
   */
  const thumbnailStyle = computed(() => {
    const pw = props.pageData.width ?? 1;
    const ph = props.pageData.height ?? 1;
    const pageRatio = pw / ph;
    if (pageRatio < 1) {
      return { aspectRatio: '1 / 1' };
    }
    return { aspectRatio: `${pw} / ${ph}` };
  });

  /**
   * 根据 pageData 宽高比，计算 canvas 在容器内的等比缩放尺寸
   * - pageRatio >= 1（横版）：容器已按页面比例，canvas 铺满
   * - pageRatio < 1（竖版）：容器为正方形，canvas 等比缩放居中
   */
  const canvasFitStyle = computed(() => {
    const pw = props.pageData.width ?? 1;
    const ph = props.pageData.height ?? 1;
    const pageRatio = pw / ph;

    if (pageRatio < 1) {
      // 竖版：容器是正方形，canvas 等比缩放居中
      return { width: `${pageRatio * 100}%`, height: '100%' };
    }
    // 横版/正方形：容器已按页面比例，canvas 铺满
    return { width: '100%', height: '100%' };
  });

  const thumbnailCanvas = ref<HTMLCanvasElement | null>(null);

  watch(
    () => props.sourceCanvas,
    (source) => {
      if (!source) return;
      const canvas = thumbnailCanvas.value;
      if (!canvas) return;

      const container = canvas.parentElement!;
      const availW = container.clientWidth;
      const availH = container.clientHeight;
      if (availW === 0 || availH === 0) return;

      const pw = props.pageData.width ?? 1;
      const ph = props.pageData.height ?? 1;
      const pageRatio = pw / ph;

      // 在容器内等比缩放，计算 canvas 渲染尺寸
      const { width: drawW, height: drawH } = getScaleWH(availW, availH, pageRatio, false);

      canvas.width = drawW;
      canvas.height = drawH;
      canvas.getContext('2d')!.drawImage(source, 0, 0, drawW, drawH);
    }
  );

  function handleClick() {
    emit('select');
  }

  function handleDelete() {
    emit('delete');
  }
</script>

<style scoped>
  .page-list-item {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    box-sizing: border-box;
    cursor: pointer;
  }

  .thumbnail-container {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid transparent;
    border-radius: 6px;
    background-color: #fbf9ff;
    box-sizing: border-box;
    overflow: hidden;
    transition: border-color 0.2s;
  }

  .page-list-item.is-selected .thumbnail-container {
    border-color: #6033e1;
  }

  .thumbnail-canvas {
    flex-shrink: 0;
    background-color: #fbf9ff;
  }

  .thumbnail-gradient {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 6px;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0) 100%);
  }

  .page-badge {
    position: absolute;
    left: 8px;
    bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 6px;
    min-width: 36px;
    height: 20px;
    border-radius: 10px;
    background-color: rgba(82, 82, 82, 0.7);
    box-sizing: border-box;
  }

  .page-badge.is-cover {
    background-color: #6033e1;
  }

  .badge-text {
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-weight: 400;
    font-size: 11px;
    font-style: normal;
    line-height: 1;
    color: #ffffff;
    white-space: nowrap;
  }

  .delete-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.45);
    color: #fff;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    display: none;
    align-items: center;
    justify-content: center;
  }

  .page-list-item:hover .delete-btn,
  .page-list-item.is-selected .delete-btn {
    display: flex;
  }
</style>
